import CancelIcon from "@mui/icons-material/Clear";
import { Box, CircularProgress, Grid, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { fetchEmployee } from "../../api/employee";
import { selectJobCodeData } from "../../slices/commonConfig/commonConfigSlice";
import { validateEmployeeDataForBidGroup } from "../../util/bid/bidInputValidation";
import { buildAddEmployeeData } from "../../util/bid/bidGroupUtils";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT, EMP_SCOPE } from "../../util/constants";
import { validateEmployee, validateEmployeeId } from "../../util/inputValidation";
import { CHAR_LIMIT_MESSAGE, COMMENTS_REQUIRED, EMPLOYEE_ID_REQUIRED } from "../../util/messageConstants";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import { getFreedayStandardReliefMap } from "../../api/scheduleEditor";

const useStyles = makeStyles((theme) => ({
  inputText: {
    width: "15vw",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
    marginTop: "20px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
  employeeDataValue: {
    fontSize: "14px",
    marginTop: "-5px",
  },
  commentBox: {
    marginTop: "25px",
  },
  employeeInfoLoading: {
    position: "relative",
    float: "left",
    top: "7px",
    marginLeft: "3vw",
  },
  freedayBidMenuStyle: {
    maxWidth: "17vw",
    width: "120px",
    fontSize: "14px",
    textAlign: "left",
    margin: "auto",
  },
}));

const AddEmployeeDialog = (props) => {
  const classes = useStyles();
  const jobCodeData = useSelector(selectJobCodeData);
  const [employeeIdSearchLoading, setEmployeeIdSearchLoading] = useState(false);
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [addEmployeeData, setAddEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeId, setEmployeeId] = useState("");

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setComments("");
    setCommentsError(false);
    setCommentErrorMessage("");
    let errors = validateEmployeeId("");
    setAddEmployeeData(null);

    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const handleCommentsChange = (event) => {
    setCommentsError(false);
    setCommentErrorMessage("");
    setComments(event.target.value);
  };

  const handleCancelClicked = () => {
    setComments("");
    setCommentsError(false);
    setCommentErrorMessage("");
    setAddEmployeeData(null);
    setEmployeeId("");
    setEmployeeIdErrors("");
    props.onClickDialogClose();
  };

  const handleEmployeeIdChange = (eid) => {
    if (addEmployeeData) {
      setAddEmployeeData(null);
    }
    setEmployeeId(eid.target.value.toLowerCase());
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event?.target?.value?.trim();
    let errors = validateEmployeeId(trimEmpId);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!addEmployeeData || trimEmpId !== addEmployeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const fetchEmployeeData = async (eid) => {
    setEmployeeIdSearchLoading(true);
    setEmployeeIdErrors("");
    setAddEmployeeData(null);
    const scope =
      props.bidType === BID_TYPE.FREEDAY || props.bidType === BID_TYPE.TWU_DAT_FTO ? EMP_SCOPE.DECLARATIONS : "";
    await fetchEmployee(eid, scope)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = result;
          if (props.bidType === BID_TYPE.FREEDAY) {
            const standardReliefMap = await getFreedayStandardReliefMap(
              props.bidStation,
              props.bidWorkgroup,
              props.schedMonth,
              props.schedYear,
              employeeData.eid
            );
            let standardRelief = "";
            if (standardReliefMap && Object.keys(standardReliefMap).includes(employeeData.eid)) {
              standardRelief = standardReliefMap[employeeData.eid];
            }
            employeeData = {
              ...employeeData,
              standardRelief: standardRelief,
            };
          }
          let employeeDataErrors = validateEmployeeDataForBidGroup(employeeData, props.bidGroup);
          if (employeeDataErrors === "") {
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.shortName,
              employeeData.lastName,
              employeeData.activeStation,
              jobCodeData[employeeData.homeAccountJobCode].workgroup
            );
          }
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
          }
          setAddEmployeeData(employeeData);
          setEmployeeIdSearchLoading(false);
        } else {
          setEmployeeIdErrors("Invalid Employee ID");
          setEmployeeIdSearchLoading(false);
        }
      })
      .catch((err) => {
        console.log(err, "error");
        setEmployeeIdSearchLoading(false);
      });
  };

  const getApplicableDeclaration = () => {
    return addEmployeeData?.declarations?.find((declarationItem) => declarationItem.year === props.schedYear)
      ?.declaration;
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleAddClicked = () => {
    if (employeeId === "") {
      setEmployeeIdErrors(EMPLOYEE_ID_REQUIRED);
    }
    if (comments === "") {
      setCommentsError(true);
      setCommentErrorMessage(COMMENTS_REQUIRED);
    } else if (comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT) {
      setCommentsError(true);
      setCommentErrorMessage(CHAR_LIMIT_MESSAGE(BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT));
    } else {
      // Employee is added for the first time, so create new comments array.(old comments on previous load will be lost as expected)
      const commentsArray = [
        {
          comments,
          createdAt: DateTime.now().toMillis().toString(),
          createdBy: `${props.userSession.lastName}, ${props.userSession.firstName}`,
          isEligible: true,
          rank: "-",
          isRemoved: false,
        },
      ];

      props.setBidGroup([
        buildAddEmployeeData(addEmployeeData, commentsArray, props.bidStatus, props.bidType),
        ...props.bidGroup,
      ]);
      setEmployeeId("");
      setComments("");
      setCommentsError(false);
      props.setShowAddEmployeeDialog(false);
      setAddEmployeeData(null);
      // scroll to top of the grid once addition is done to see added item.
      props.apiRef.current.scrollToIndexes({
        rowIndex: 0,
      });
    }
  };

  return (
    <Dialog
      fullWidth
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleCancelClicked}
      maxWidth={"md"}
      sx={{ margin: "auto" }}
    >
      <Box p={1}>
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent>
          <Typography fontSize={"14px"}>
            Adding Employees to the Bid Group will not affect the Seniority List.
          </Typography>
          <Grid container>
            <TextField
              className={classes.inputText}
              label="Employee ID"
              variant="outlined"
              value={employeeId}
              onChange={handleEmployeeIdChange}
              onBlur={handleEmployeeIdBlur}
              onKeyUp={handleEmployeeIdEnterKeyPressed}
              InputProps={{
                endAdornment:
                  employeeId !== "" ? (
                    <InputAdornment position="start">
                      <CancelButtonEmployeeId />
                    </InputAdornment>
                  ) : (
                    ""
                  ),
              }}
              error={employeeIdErrors !== ""}
              helperText={employeeIdErrors}
            />
          </Grid>
          {!employeeIdSearchLoading ? (
            addEmployeeData &&
            !employeeIdErrors && (
              <Grid
                container
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  marginTop: 3,
                  backgroundColor: "secondary.swaGray1",
                  height: "auto",
                  borderRadius: "5px",
                }}
              >
                <Grid item xs={4} sx={{ fontSize: "16px", paddingLeft: "16px", textAlign: "left" }}>
                  {addEmployeeData.lastName + ", " + addEmployeeData.shortName}
                </Grid>

                <Grid item xs={8} sx={{ textAlign: "left", maxHeight: "56px" }}>
                  <Stack direction="row">
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        HOME STATION
                      </Typography>
                      <p className={classes.employeeDataValue}>{addEmployeeData.homeStation}</p>
                    </Box>
                    {addEmployeeData.tempStation && (
                      <Box ml={2} mr={2}>
                        <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                          TEMP STATION
                        </Typography>
                        <p className={classes.employeeDataValue}>{addEmployeeData.tempStation}</p>
                      </Box>
                    )}
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        WORKGROUP
                      </Typography>
                      <p className={classes.employeeDataValue}>
                        {getWorkgroupGivenJobCode(addEmployeeData.homeAccountJobCode)}
                      </p>
                    </Box>
                    <Box ml={2} mr={2}>
                      <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                        STATUS
                      </Typography>
                      <p className={classes.employeeDataValue}>{addEmployeeData.ftpt}</p>
                    </Box>
                    {(props.bidType === BID_TYPE.FREEDAY || props.bidType === BID_TYPE.TWU_DAT_FTO) && (
                      <Box ml={2} mr={2}>
                        <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                          DECLARATION
                        </Typography>
                        <p className={classes.employeeDataValue}>{getApplicableDeclaration() || "-"}</p>
                      </Box>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            )
          ) : (
            <CircularProgress className={classes.employeeInfoLoading} size={24} />
          )}
          <TextField
            className={classes.commentBox}
            value={comments}
            multiline
            fullWidth
            rows={2}
            label="Comments"
            placeholder="Enter comments here"
            InputLabelProps={{ shrink: true }}
            onChange={handleCommentsChange}
            error={commentsError}
            helperText={commentErrorMessage}
          />
          {comments.length > BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT ? (
            <Box sx={{ textAlign: "right", color: "primary.swaRed" }}>
              {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
            </Box>
          ) : (
            <Box sx={{ textAlign: "right" }}>
              {comments.length} / {BID_GROUP_ADD_EMPLOYEE_CHAR_LIMIT}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            id="cancelButton"
            sx={{ width: "100px" }}
            variant="outlined"
            color="primary"
            onClick={handleCancelClicked}
            disabled={employeeIdSearchLoading}
            size="large"
          >
            Cancel
          </Button>
          <Button
            id="addButton"
            sx={{ width: "100px" }}
            variant="contained"
            color="primary"
            onClick={handleAddClicked}
            disabled={employeeIdErrors || employeeIdSearchLoading}
            size="large"
          >
            Add
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

AddEmployeeDialog.propTypes = {
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
  bidGroup: PropTypes.array.isRequired,
  setShowAddEmployeeDialog: PropTypes.func.isRequired,
  apiRef: PropTypes.object.isRequired,
  bidStatus: PropTypes.string.isRequired,
  userSession: PropTypes.object.isRequired,
  bidType: PropTypes.string.isRequired,
  schedYear: PropTypes.string,
  schedMonth: PropTypes.string,
  bidStation: PropTypes.string,
  bidWorkgroup: PropTypes.string,
};

export default AddEmployeeDialog;
